import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { User } from '../models';
import { WhitelabelConfigStore } from '../core/config/whitelabel-config.store';
import { UserService } from '../services/user.service';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: [
    '../my-booking/my-booking.component.css',
    './user-profile.component.css'
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UserProfileComponent implements OnInit {
  isLoggedIn = false;
  user: User | null = null;
  profileLoading = false;
  profileSaving = false;
  isEditing = false;
  errorMessage = '';
  successMessage = '';
  sidebarOpen = false;
  profileForm: FormGroup;

  constructor(
    private users: UserService,
    private router: Router,
    private formBuilder: FormBuilder,
    public readonly configStore: WhitelabelConfigStore
  ) {
    this.profileForm = this.formBuilder.group({
      first_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50), Validators.pattern(/.*\S.*/)]],
      last_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50), Validators.pattern(/.*\S.*/)]],
      email: ['', [Validators.email, Validators.maxLength(120)]],
      phone_number: ['', [
        Validators.required,
        Validators.pattern(/^\+?(?=(?:\D*\d){7,15}\D*$)[0-9 ()-]+$/)
      ]]
    });
  }

  ngOnInit(): void {
    const storedUser = this.readStoredUser();
    this.isLoggedIn =
      sessionStorage.getItem('isLoggedInEasy') === 'true' ||
      localStorage.getItem('isLoggedInEasy') === 'true';

    if (!this.isLoggedIn || !storedUser) {
      this.isLoggedIn = false;
      return;
    }

    this.user = new User().deserialize(storedUser);
    this.populateProfileForm();
    this.loadProfile();
  }

  get config() {
    return this.configStore.snapshot.config;
  }

  get logo(): string {
    return this.config?.self.logoLight || this.config?.self.logo || 'assets/images/logo.webp';
  }

  get operatorName(): string {
    return this.config?.site.name || 'Mash Poa';
  }

  get userName(): string {
    return [this.user?.first_name, this.user?.last_name].filter(Boolean).join(' ') || 'Traveller';
  }

  get userInitials(): string {
    return [this.user?.first_name, this.user?.last_name]
      .filter(Boolean)
      .map(value => String(value).trim().charAt(0).toUpperCase())
      .join('') || 'T';
  }

  get memberSince(): string {
    const value = this.user?.created_at;
    if (!value) return 'Member account';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Member account';
    return `Member since ${new Intl.DateTimeFormat('en-KE', {
      month: 'short',
      year: 'numeric'
    }).format(date)}`;
  }

  loadProfile(): void {
    if (!this.user) return;
    this.profileLoading = true;
    this.errorMessage = '';
    this.users.getProfile(this.user).subscribe({
      next: (response: any) => {
        this.profileLoading = false;
        const profileData = response?.data?.profile || response?.data || {};
        const profile = {
          ...this.user,
          ...profileData,
          token: profileData?.token || this.user?.token
        };
        this.user = new User().deserialize(profile);
        this.persistProfile(profile);
        this.populateProfileForm();
      },
      error: () => {
        this.profileLoading = false;
        this.populateProfileForm();
        this.errorMessage = 'We could not refresh your profile. The saved account details are shown below.';
      }
    });
  }

  editProfile(): void {
    this.populateProfileForm();
    this.errorMessage = '';
    this.successMessage = '';
    this.isEditing = true;
  }

  cancelEditing(): void {
    this.populateProfileForm();
    this.errorMessage = '';
    this.isEditing = false;
  }

  saveProfile(): void {
    if (!this.user || this.profileSaving) return;
    this.profileForm.markAllAsTouched();
    if (this.profileForm.invalid) return;

    const value = this.profileForm.getRawValue();
    const payload = {
      first_name: String(value.first_name || '').trim(),
      last_name: String(value.last_name || '').trim(),
      email: String(value.email || '').trim(),
      phone_number: String(value.phone_number || '').trim()
    };
    this.profileSaving = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.users.updateProfile(this.user, payload).subscribe({
      next: (response: any) => {
        this.profileSaving = false;
        const returnedProfile = response?.data?.profile || response?.data || {};
        const profile = {
          ...this.user,
          ...payload,
          ...returnedProfile,
          token: returnedProfile?.token || this.user?.token
        };
        this.user = new User().deserialize(profile);
        this.persistProfile(profile);
        this.populateProfileForm();
        this.isEditing = false;
        this.successMessage = 'Your profile has been updated successfully.';
      },
      error: (error: any) => {
        this.profileSaving = false;
        this.errorMessage =
          error?.error?.message ||
          error?.error?.detail ||
          'We could not update your profile. Please check the details and try again.';
      }
    });
  }

  openSidebar(): void {
    this.sidebarOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
    document.body.style.overflow = '';
  }

  bookBus(): void {
    this.closeSidebar();
    this.router.navigate(['/']);
  }

  contactSupport(): void {
    this.router.navigate(['/contact-us']);
  }

  logoutUser(): void {
    localStorage.removeItem('userEasy');
    sessionStorage.removeItem('userEasy');
    sessionStorage.setItem('isLoggedInEasy', 'false');
    localStorage.setItem('isLoggedInEasy', 'false');
    document.body.style.overflow = '';
    this.router.navigate(['/']);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.sidebarOpen) this.closeSidebar();
  }

  private readStoredUser(): any | null {
    const value = sessionStorage.getItem('userEasy') || localStorage.getItem('userEasy');
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private populateProfileForm(): void {
    this.profileForm.reset({
      first_name: this.user?.first_name || '',
      last_name: this.user?.last_name || '',
      email: this.user?.email || '',
      phone_number: this.user?.phone_number || ''
    });
  }

  private persistProfile(profile: any): void {
    localStorage.setItem('userEasy', JSON.stringify(profile));
    sessionStorage.setItem('userEasy', JSON.stringify(profile));
  }
}
